import { createContext, useContext, type ReactNode } from 'react';
import {
  getCurrentUserControllerGetCurrentUserSuspenseQueryOptions,
  useCurrentUserControllerGetCurrentUserSuspense,
} from './generated/current-user/current-user.ts';
import type {
  CurrentUserResponseDto,
  CurrentUserResponseDtoPartnersItem,
} from './generated/schemas/index.ts';

export interface AuthContextValue {
  /** The authenticated user's profile and metadata. */
  user: CurrentUserResponseDto;
  /** Partners the user has access to. */
  partners: CurrentUserResponseDtoPartnersItem[];
  /** The currently selected partner key (from the artic_partner cookie). */
  activePartner: string | undefined;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Re-export the generated suspense query options so route loaders
 * can prefetch the current user without importing from generated paths.
 */
export const currentUserQueryOptions =
  getCurrentUserControllerGetCurrentUserSuspenseQueryOptions;

// ── Provider ──

interface AuthProviderProps {
  children: ReactNode;
  /** The currently active partner key (read from the artic_partner cookie). */
  activePartner: string | undefined;
}

/**
 * Fetches the current user via the nest-backend `/current-user` endpoint
 * and exposes user, partners, and active partner via React context.
 *
 * Must be rendered inside a `<QueryClientProvider>` and wrapped with a
 * `<Suspense>` boundary (loading state) and an error boundary.
 */
export function AuthProvider({ children, activePartner }: AuthProviderProps) {
  const { data: response } = useCurrentUserControllerGetCurrentUserSuspense();

  // customFetch throws on non-2xx, so useSuspenseQuery always resolves
  // to the success variant. Cast is safe here.
  const user = response.data as CurrentUserResponseDto;

  return (
    <AuthContext.Provider value={{ user, partners: user.partners, activePartner }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──

/**
 * Returns the current authenticated user, their partners, and the active partner.
 * Must be used within an `<AuthProvider>`.
 */
export function useCurrentUser(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useCurrentUser must be used within an <AuthProvider>');
  }
  return ctx;
}

/**
 * True when the authenticated user is an Artic admin — i.e. their email sits on
 * an admin domain (artic.works / trunk.works). Mirrors the nest-backend
 * `isAdminUser` rule (exact final-segment match). Admin-only UI such as the
 * Change Partner switcher gates on this. Must be used within an `<AuthProvider>`.
 */
export function useIsArticAdmin(): boolean {
  const { user } = useCurrentUser();
  const domain = user.email.toLowerCase().split('@').at(-1);
  return domain === 'artic.works' || domain === 'trunk.works';
}
